/**
 * Gap Analysis Service
 * Handles all gap analysis backlog and workshop API calls
 */

import axios from 'axios';

// Use the same backend URL detection logic as api.js
const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? window.location.protocol + '//' + window.location.hostname + ':5001'
  : window.location.protocol + '//' + window.location.hostname + '/api';

console.log('Gap Analysis Service - Using backend URL:', API_BASE_URL);

// Configure axios instance with credentials for authentication
axios.defaults.withCredentials = true;

// ==================== Backlog Items ====================

export const uploadBacklogItemsExcel = async (projectId, file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await axios.post(
    `${API_BASE_URL}/gap-analysis/backlog-items/${projectId}/upload`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  );
  return response.data;
};

export const getBacklogItems = async (projectId) => {
  const response = await axios.get(`${API_BASE_URL}/gap-analysis/backlog-items/${projectId}`);
  return response.data;
};

export const getWorkshopRequiredItems = async (projectId) => {
  const response = await axios.get(`${API_BASE_URL}/gap-analysis/backlog-items/${projectId}/workshop-required`);
  return response.data;
};

export const getBacklogItemAttendees = async (itemId, projectId) => {
  const response = await axios.get(`${API_BASE_URL}/gap-analysis/backlog-items/${itemId}/attendees/${projectId}`);
  return response.data;
};

export const updateBacklogItem = async (itemId, data) => {
  const response = await axios.put(`${API_BASE_URL}/gap-analysis/backlog-items/${itemId}`, data);
  return response.data;
};

export const deleteBacklogItem = async (itemId) => {
  const response = await axios.delete(`${API_BASE_URL}/gap-analysis/backlog-items/${itemId}`);
  return response.data;
};

export const clearBacklogItems = async (projectId) => {
  const response = await axios.delete(`${API_BASE_URL}/gap-analysis/backlog-items/${projectId}/clear`);
  return response.data;
};

export const previewBacklogReupload = async (projectId, file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await axios.post(
    `${API_BASE_URL}/gap-analysis/backlog-items/${projectId}/reupload-preview`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  );
  return response.data;
};

export const applyBacklogReupload = async (projectId, file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await axios.post(
    `${API_BASE_URL}/gap-analysis/backlog-items/${projectId}/reupload-apply`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  );
  return response.data;
};

// ==================== Workshop Names ====================

export const getWorkshopNames = async (projectId) => {
  const response = await axios.get(`${API_BASE_URL}/gap-analysis/workshop-names/${projectId}`);
  return response.data;
};

export const createWorkshopName = async (projectId, workshopName) => {
  const response = await axios.post(`${API_BASE_URL}/gap-analysis/workshop-names/${projectId}`, {
    workshop_name: workshopName,
    created_by_user: true
  });
  return response.data;
};

export const updateWorkshopName = async (workshopNameId, newName) => {
  const response = await axios.put(`${API_BASE_URL}/gap-analysis/workshop-names/${workshopNameId}`, {
    workshop_name: newName
  });
  return response.data;
};

export const deleteWorkshopName = async (workshopNameId) => {
  const response = await axios.delete(`${API_BASE_URL}/gap-analysis/workshop-names/${workshopNameId}`);
  return response.data;
};

export const moveWorkshopUp = async (workshopNameId) => {
  const response = await axios.put(`${API_BASE_URL}/gap-analysis/workshop-names/${workshopNameId}/move-up`);
  return response.data;
};

export const moveWorkshopDown = async (workshopNameId) => {
  const response = await axios.put(`${API_BASE_URL}/gap-analysis/workshop-names/${workshopNameId}/move-down`);
  return response.data;
};

// ==================== Workshop Mappings ====================

export const getWorkshopMappings = async (projectId) => {
  const response = await axios.get(`${API_BASE_URL}/gap-analysis/mappings/${projectId}`);
  return response.data;
};

export const createWorkshopMapping = async (workshopNameId, backlogItemId) => {
  const response = await axios.post(`${API_BASE_URL}/gap-analysis/mappings`, {
    workshop_name_id: workshopNameId,
    backlog_item_id: backlogItemId
  });
  return response.data;
};

export const deleteWorkshopMapping = async (mappingId) => {
  const response = await axios.delete(`${API_BASE_URL}/gap-analysis/mappings/${mappingId}`);
  return response.data;
};

// ==================== Workshop Details ====================

export const getWorkshops = async (projectId) => {
  const response = await axios.get(`${API_BASE_URL}/gap-analysis/workshops/${projectId}`);
  return response.data;
};

export const createWorkshop = async (workshopData) => {
  const response = await axios.post(`${API_BASE_URL}/gap-analysis/workshops`, workshopData);
  return response.data;
};

export const updateWorkshop = async (workshopId, data) => {
  const response = await axios.put(`${API_BASE_URL}/gap-analysis/workshops/${workshopId}`, data);
  return response.data;
};

// ==================== Workshop Schedules ====================

export const getSchedules = async (projectId) => {
  const response = await axios.get(`${API_BASE_URL}/gap-analysis/schedules/${projectId}`);
  return response.data;
};

export const createSchedule = async (scheduleData) => {
  const response = await axios.post(`${API_BASE_URL}/gap-analysis/schedules`, scheduleData);
  return response.data;
};

export const updateSchedule = async (scheduleId, data) => {
  const response = await axios.put(`${API_BASE_URL}/gap-analysis/schedules/${scheduleId}`, data);
  return response.data;
};

export const deleteSchedule = async (scheduleId) => {
  const response = await axios.delete(`${API_BASE_URL}/gap-analysis/schedules/${scheduleId}`);
  return response.data;
};

// ==================== Excel Export/Import ====================

export const exportSchedules = async (projectId) => {
  const response = await axios.get(
    `${API_BASE_URL}/gap-analysis/schedules/${projectId}/export`,
    { responseType: 'blob' }
  );
  return response.data;
};

export const previewScheduleImport = async (projectId, file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await axios.post(
    `${API_BASE_URL}/gap-analysis/schedules/${projectId}/import-preview`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  );
  return response.data;
};

export const applyScheduleImport = async (projectId, file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await axios.post(
    `${API_BASE_URL}/gap-analysis/schedules/${projectId}/import-apply`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  );
  return response.data;
};

// ==================== Auto-Scheduling ====================

export const autoScheduleWorkshops = async (projectId, config) => {
  const response = await axios.post(
    `${API_BASE_URL}/gap-analysis/schedules/${projectId}/auto-schedule`,
    config,
    { timeout: 60000 } // 1 minute timeout for auto-scheduling
  );
  return response.data;
};

// ==================== Calendar Invites ====================

export const generateGoogleMeetLinks = async (projectId) => {
  const response = await axios.post(
    `${API_BASE_URL}/gap-analysis/schedules/${projectId}/generate-google-meet`
  );
  return response.data;
};

export const downloadAllICS = async (projectId) => {
  const response = await axios.get(
    `${API_BASE_URL}/gap-analysis/schedules/${projectId}/download-ics`,
    { responseType: 'blob' }
  );

  // Create a download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;

  // Extract filename from Content-Disposition header or use default
  const contentDisposition = response.headers['content-disposition'];
  let filename = `Gap_Analysis_Calendar_Invites_${new Date().toISOString().split('T')[0]}.zip`;
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, '');
    }
  }

  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);

  return { success: true, filename };
};

export const downloadSingleIcs = async (scheduleId) => {
  const response = await axios.get(
    `${API_BASE_URL}/gap-analysis/schedules/${scheduleId}/download-single-ics`,
    { responseType: 'blob' }
  );
  return response.data;
};
